import { Link, useLocation } from "react-router-dom";
import { OndaAvatar } from "./OndaAvatar";
import { PresenceDot } from "./Presence";
import { VerifiedBadge } from "./VerifiedBadge";
import { MessageSquareText, Users } from "lucide-react";
import { useChat } from "../context/ChatContext";

function formatWhen(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  if (sameDay) {
    const h = String(d.getHours()).padStart(2, "0");
    const m = String(d.getMinutes()).padStart(2, "0");
    return `${h}:${m}`;
  }
  const diffMs = now - d;
  const oneDay = 86400000;
  if (diffMs < 7 * oneDay) {
    return d.toLocaleDateString(undefined, { weekday: "short" });
  }
  return d.toLocaleDateString(undefined, { day: "2-digit", month: "2-digit" });
}

export function ConversationList({ conversations, currentUserId, loading }) {
  const location = useLocation();
  const { getPresence } = useChat();
  const activeId = location.pathname.startsWith("/chat/")
    ? location.pathname.split("/").pop()
    : null;

  if (loading && conversations.length === 0) {
    return (
      <div className="q-glass rounded-3xl p-6 flex items-center gap-3" data-testid="conv-list-loading">
        <span className="h-2 w-2 rounded-full q-accent-bg animate-pulse" />
        <span className="text-sm text-slate-300">Cargando conversaciones…</span>
      </div>
    );
  }

  if (conversations.length === 0) {
    return (
      <div className="q-glass rounded-3xl p-6" data-testid="conv-list-empty">
        <div className="flex items-start gap-3">
          <div
            className="h-11 w-11 rounded-2xl flex items-center justify-center border border-white/10"
            style={{ background: "var(--accent-soft)" }}
          >
            <MessageSquareText size={20} className="q-accent-text" />
          </div>
          <div>
            <h2 className="font-display text-lg font-semibold">Sin conversaciones aún</h2>
            <p className="text-sm text-slate-300 mt-1">
              Busca a alguien por su <span className="font-mono">@OndaTag</span> o correo para iniciar tu primer chat.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <ul className="q-glass rounded-3xl overflow-hidden divide-y divide-white/5" data-testid="conv-list">
      {conversations.map((c) => {
        const isGroup = c.type === "group";
        const isActive = c.id === activeId;
        const preview = c.last_message?.text || "Sin mensajes aún";
        const isMine = c.last_message?.sender_id === currentUserId;
        const lastSender = c.last_message_sender;
        const testKey = isGroup ? c.id : c.other?.ondatag;
        const displayName = isGroup ? c.title : c.other?.display_name;
        const ondatag = isGroup ? null : c.other?.ondatag;
        const avatarUrl = isGroup ? "" : c.other?.avatar;
        const verified = !isGroup && c.other?.is_verified_business;
        const pres = !isGroup && c.other ? getPresence(c.other.id) : null;
        const online = pres ? pres.is_online || c.other.is_online : false;

        return (
          <li key={c.id}>
            <Link
              to={`/chat/${c.id}`}
              data-testid={`conv-item-${testKey}`}
              className={
                "flex items-center gap-3 px-4 py-3 transition-colors " +
                (isActive ? "bg-white/[0.04]" : "hover:bg-white/5")
              }
            >
              <div className="relative">
                {isGroup ? (
                  <div
                    className="h-[46px] w-[46px] rounded-2xl flex items-center justify-center border border-white/10 bg-gradient-to-br from-slate-700 to-slate-900"
                    style={{ boxShadow: "inset 0 0 12px var(--accent-soft)" }}
                  >
                    <Users size={22} className="q-accent-text" />
                  </div>
                ) : (
                  <OndaAvatar src={avatarUrl} name={displayName} size={46} />
                )}
                {!isGroup && (
                  <span
                    className="absolute -bottom-0.5 -right-0.5"
                    data-testid={`conv-presence-${testKey}`}
                  >
                    <PresenceDot isOnline={online} size={12} />
                  </span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-sm font-semibold text-slate-100 truncate flex items-center gap-1">
                    {displayName}
                    {verified && <VerifiedBadge size={12} />}
                  </span>
                  <span className="text-[11px] font-mono text-slate-400 shrink-0">
                    {formatWhen(c.last_message?.timestamp || c.updated_at)}
                  </span>
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-xs text-slate-300 truncate flex-1">
                    {isGroup && lastSender && !isMine ? (
                      <span className="text-slate-500">{lastSender.display_name.split(" ")[0]}: </span>
                    ) : isMine && c.last_message ? (
                      <span className="text-slate-500">Tú: </span>
                    ) : null}
                    {preview}
                  </span>
                  {c.unread_count > 0 && (
                    <span
                      className="text-[10px] font-bold rounded-full px-2 py-0.5 q-accent-bg text-[color:var(--accent-contrast)]"
                      data-testid={`conv-unread-${testKey}`}
                    >
                      {c.unread_count}
                    </span>
                  )}
                </div>
                <div className="font-mono text-[10px] text-slate-500 mt-0.5">
                  {isGroup ? `${c.member_count} miembros` : `@${ondatag}`}
                </div>
              </div>
            </Link>
          </li>
        );
      })}
    </ul>
  );
}
